// Address Step - Full Form (Kustom Style)
// All address fields in one clean form

import { useState, useEffect, useRef } from 'react';
import { useCheckoutStore } from '../store/checkout';
import { lookupPostalCode, updateSession, saveAddress, deleteCustomerData } from '../api/client';
import { getMarketConfig } from '../config/market';
import { Input } from '../components/Input';
import { Button } from '../components/Button';
import styles from './AddressStep.module.css';

export function AddressStep() {
  const customer = useCheckoutStore((s) => s.customer);
  const shippingAddress = useCheckoutStore((s) => s.shippingAddress);
  const billingAddress = useCheckoutStore((s) => s.billingAddress);
  const merchant = useCheckoutStore((s) => s.merchant);
  const sessionId = useCheckoutStore((s) => s.sessionId);
  const locale = useCheckoutStore((s) => s.locale);
  const setCustomer = useCheckoutStore((s) => s.setCustomer);
  const setShippingAddress = useCheckoutStore((s) => s.setShippingAddress);
  const nextStep = useCheckoutStore((s) => s.nextStep);
  const prevStep = useCheckoutStore((s) => s.prevStep);
  const isLoading = useCheckoutStore((s) => s.isLoading);
  const setLoading = useCheckoutStore((s) => s.setLoading);
  const savedAddresses = useCheckoutStore((s) => s.savedAddresses);
  const setSavedAddresses = useCheckoutStore((s) => s.setSavedAddresses);
  
  // Market config for locale-aware labels
  const market = getMarketConfig(locale);
  const L = market.labels;
  
  // Get postal code from store (set in previous step)
  const postalCode = shippingAddress?.postalCode || '';
  
  // Form state. Seeded from the unsubmitted draft so a reload part-way through
  // does not empty every field — see `draft` in the checkout store.
  const draft = useCheckoutStore((s) => s.draft);
  const setDraft = useCheckoutStore((s) => s.setDraft);
  const [firstName, setFirstName] = useState(draft.firstName || '');
  const [lastName, setLastName] = useState(draft.lastName || '');
  const [careOf, setCareOf] = useState(draft.careOf || '');
  const [streetAddress, setStreetAddress] = useState(draft.streetAddress || '');
  const [city, setCity] = useState('');
  const [phone, setPhone] = useState(draft.phone || '');
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  // GDPR manage-data dialog state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  
  // Track whether we've already pre-filled to avoid stale dep issues
  const hasPrefilled = useRef(false);
  
  // Pre-fill from saved addresses if available
  useEffect(() => {
    if (hasPrefilled.current) return;
    
    if (savedAddresses && savedAddresses.length > 0) {
      const defaultAddr = savedAddresses.find(a => a.isDefault) || savedAddresses[0];
      if (defaultAddr) {
        hasPrefilled.current = true;
        if (defaultAddr.fullName) {
          const parts = defaultAddr.fullName.split(' ');
          if (parts.length >= 2) {
            setFirstName(parts[0]);
            setLastName(parts.slice(1).join(' '));
          } else if (parts.length === 1) {
            setFirstName(parts[0]);
          }
        }
        if (defaultAddr.phone) setPhone(defaultAddr.phone);
        if (defaultAddr.streetAddress) setStreetAddress(defaultAddr.streetAddress);
        if (defaultAddr.city) setCity(defaultAddr.city);
      }
    }
    /**
     * Prefer the delivery address, fall back to the billing one.
     *
     * A shop selling digital goods sends `skipShipping` and a billing address
     * and nothing else — there is no delivery, so there is no delivery address
     * to prefill from, and the customer was asked to type an address the shop
     * had already supplied. The address collected here is the one the card
     * needs, which is the billing address, so that is the right fallback.
     */
    const prefillFrom = shippingAddress?.streetAddress ? shippingAddress : billingAddress;
    if (prefillFrom?.streetAddress) setStreetAddress(prefillFrom.streetAddress);
    if (prefillFrom?.city) setCity(prefillFrom.city);
    // The C/O line survives a lookup or a back-navigation like every other
    // field. It was the only one dropped, so a customer who filled it in and
    // stepped back found it blank.
    if (prefillFrom?.careOf) setCareOf(prefillFrom.careOf);
  }, [savedAddresses, shippingAddress, billingAddress]);
  
  // Pre-fill name from customer if available (only once)
  const hasPrefilledCustomer = useRef(false);
  useEffect(() => {
    if (hasPrefilledCustomer.current) return;
    // Prefer the split we already know over guessing it again. A returning
    // customer who typed "Anne Lise" / "Hansen" had it rebuilt from the joined
    // string on every visit, and came back as "Anne" / "Lise Hansen".
    if (customer?.firstName || customer?.lastName) {
      hasPrefilledCustomer.current = true;
      if (customer.firstName) setFirstName(customer.firstName);
      if (customer.lastName) setLastName(customer.lastName);
    } else if (customer?.name) {
      hasPrefilledCustomer.current = true;
      const parts = customer.name.split(' ');
      if (parts.length >= 2) {
        setFirstName(parts[0]);
        setLastName(parts.slice(1).join(' '));
      } else if (parts.length === 1) {
        setFirstName(parts[0]);
      }
    }
    if (customer?.phone) {
      setPhone(customer.phone);
    }
  }, [customer]);

  // Resolve city from postal code.
  //
  // Keyed on the postal code, not on whether city is empty. The previous
  // version bailed whenever a city was already present, so changing the
  // postal code after the city had been filled left the old city in place —
  // 0150 gave Oslo, then editing to 5003 still said Oslo, and that wrong city
  // shipped with the order.
  //
  // cityFromPostalCode remembers which lookup produced the current value, so
  // a city the customer typed themselves is never overwritten.
  const cityFromPostalCode = useRef<string | null>(null);
  const lookupSeq = useRef(0);

  useEffect(() => {
    if (!postalCode || !market.postalCodePattern.test(postalCode)) return;

    // Already resolved for this exact postal code.
    if (cityFromPostalCode.current === postalCode) return;

    // A city that did not come from a lookup was entered by the customer or
    // restored from a saved address — leave it alone.
    if (city && cityFromPostalCode.current === null) return;

    const seq = ++lookupSeq.current;

    lookupPostalCode(postalCode)
      .then((response) => {
        // Discard a slow response for a postal code the customer has since
        // changed, otherwise the older answer wins the race.
        if (seq !== lookupSeq.current) return;
        if (response.data?.city) {
          cityFromPostalCode.current = postalCode;
          setCity(response.data.city);
        }
      })
      .catch(() => {
        // Unknown postal code — the customer fills the city in themselves.
      });
  }, [postalCode, city]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const newErrors: Record<string, string> = {};
    
    if (!firstName.trim()) newErrors.firstName = locale === 'sv' ? 'Förnamn krävs' : locale === 'da' ? 'Fornavn er påkrævet' : locale === 'fi' ? 'Etunimi vaaditaan' : locale === 'en' ? 'First name is required' : 'Fornavn er påkrevd';
    if (!lastName.trim()) newErrors.lastName = locale === 'sv' ? 'Efternamn krävs' : locale === 'da' ? 'Efternavn er påkrævet' : locale === 'fi' ? 'Sukunimi vaaditaan' : locale === 'en' ? 'Last name is required' : 'Etternavn er påkrevd';
    if (!streetAddress.trim()) newErrors.streetAddress = L.streetRequired;
    if (!city.trim()) newErrors.city = L.cityRequired;
    if (!phone.trim()) newErrors.phone = locale === 'sv' ? 'Mobilnummer krävs' : locale === 'da' ? 'Mobilnummer er påkrævet' : locale === 'fi' ? 'Puhelinnumero vaaditaan' : locale === 'en' ? 'Phone number is required' : 'Mobiltelefonnummer er påkrevd';
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    setLoading(true);
    
    const fullName = `${firstName.trim()} ${lastName.trim()}`.trim();
    const customerData = {
      email: customer?.email || '',
      name: fullName,
      // Sent alongside the joined form, because joining is one-way. The server
      // split `name` on whitespace, so anyone with two given names or a
      // two-word surname had it cut in the wrong place on their order.
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      phone,
    };
    const addressData = {
      streetAddress,
      postalCode,
      city,
      countryCode: market.countryCode,
      ...(careOf.trim() && { careOf: careOf.trim() }),
    };
    
    // Update session in backend with customer and address data
    if (sessionId) {
      try {
        await updateSession(sessionId, {
          customer: {
            email: customerData.email,
            name: { fullName, firstName: customerData.firstName, lastName: customerData.lastName },
            phone: customerData.phone,
          },
          shippingAddress: addressData,
        });
        
        // Always save address for future use (like Klarna/Kustom)
        if (customer?.email) {
          try {
            await saveAddress(customer.email, addressData, {
              fullName,
              phone,
              isDefault: true,
              consent: true,
            });
          } catch (saveErr) {
            // Failed to save address — non-critical
          }
        } else {
          // No email available for saving address
        }
      } catch (e) {
        // Failed to update session
      }
    }
    
    // Update local state
    setCustomer(customerData);
    setShippingAddress(addressData);
    
    setLoading(false);
    nextStep();
  };

  // Handle GDPR delete of all saved data
  const handleDeleteData = async () => {
    if (!customer?.email) return;
    setIsDeleting(true);
    try {
      if (!sessionId) return;
      await deleteCustomerData(customer.email, sessionId);
      setDeleteSuccess(true);
      // Clear local state so form is empty
      setSavedAddresses([]);
      setFirstName('');
      setLastName('');
      setStreetAddress('');
      setCity('');
      setPhone('');
      setCareOf('');
      hasPrefilled.current = false;
      // Auto-close after a moment
      setTimeout(() => {
        setShowDeleteDialog(false);
        setDeleteSuccess(false);
      }, 2000);
    } catch (err) {
      // GDPR delete failed
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>{locale === 'no' || locale === 'nb-NO' ? 'Dine opplysninger' : locale === 'sv' ? 'Dina uppgifter' : locale === 'da' ? 'Dine oplysninger' : locale === 'fi' ? 'Tietosi' : 'Your details'}</h1>
      <p className={styles.subtitle}>
        {locale === 'sv' ? 'Fält märkta med stjärna (*) är obligatoriska.' : locale === 'da' ? 'Felter markeret med stjerne (*) skal udfyldes.' : locale === 'fi' ? 'Tähdellä (*) merkityt kentät ovat pakollisia.' : locale === 'en' ? 'Fields marked with an asterisk (*) are required.' : 'Felt som er merket med stjerne (*) må fylles ut.'}
      </p>

      <form onSubmit={handleSubmit} className={styles.form}>
        {/* Email (read-only from previous step) */}
        <Input
          label={L.emailLabel}
          type="email"
          value={customer?.email || ''}
          readOnly
          required
        />
        
        {/* Postal code (read-only from previous step) */}
        <Input
          label={L.postalCodeLabel}
          type="text"
          value={postalCode}
          readOnly
          required
        />
        
        {/* Name row */}
        <div className={styles.inputRow}>
          <Input
            label={locale === 'sv' ? 'Förnamn' : locale === 'da' ? 'Fornavn' : locale === 'fi' ? 'Etunimi' : locale === 'en' ? 'First name' : 'Fornavn'}
            type="text"
            value={firstName}
            onChange={(e) => {
              setFirstName(e.target.value);
              setDraft({ firstName: e.target.value });
              setErrors(prev => ({ ...prev, firstName: '' }));
            }}
            error={errors.firstName}
            required
            autoComplete="given-name"
          />
          <Input
            label={locale === 'sv' ? 'Efternamn' : locale === 'da' ? 'Efternavn' : locale === 'fi' ? 'Sukunimi' : locale === 'en' ? 'Last name' : 'Etternavn'}
            type="text"
            value={lastName}
            onChange={(e) => {
              setLastName(e.target.value);
              setDraft({ lastName: e.target.value });
              setErrors(prev => ({ ...prev, lastName: '' }));
            }}
            error={errors.lastName}
            required
            autoComplete="family-name"
          />
        </div>
        
        {/* C/O (optional) */}
        <Input
          label={locale === 'sv' ? 'C/O (valfritt)' : locale === 'da' ? 'C/O (valgfrit)' : locale === 'fi' ? 'C/O (valinnainen)' : locale === 'en' ? 'C/O (optional)' : 'C/O (valgfritt)'}
          type="text"
          value={careOf}
          onChange={(e) => { setCareOf(e.target.value); setDraft({ careOf: e.target.value }); }}
          autoComplete="off"
        />
        
        {/* Street address */}
        <Input
          label={L.streetLabel}
          type="text"
          value={streetAddress}
          onChange={(e) => {
            setStreetAddress(e.target.value);
            setDraft({ streetAddress: e.target.value });
            setErrors(prev => ({ ...prev, streetAddress: '' }));
          }}
          error={errors.streetAddress}
          required
          autoComplete="street-address"
        />
        
        {/* City */}
        <Input
          label={L.cityLabel}
          type="text"
          value={city}
          data-testid="city"
          onChange={(e) => {
            setCity(e.target.value);
            // Their entry outranks the register from here on.
            cityFromPostalCode.current = null;
            setErrors(prev => ({ ...prev, city: '' }));
          }}
          error={errors.city}
          required
          autoComplete="address-level2"
        />
        
        {/* Phone */}
        <Input
          label={locale === 'sv' ? 'Mobilnummer' : locale === 'da' ? 'Mobilnummer' : locale === 'fi' ? 'Puhelinnumero' : locale === 'en' ? 'Phone number' : 'Mobiltelefonnummer'}
          type="tel"
          inputMode="numeric"
          value={phone}
          onChange={(e) => {
            const value = e.target.value.replace(/[^\d\s+]/g, '');
            setPhone(value);
            setDraft({ phone: value });
            setErrors(prev => ({ ...prev, phone: '' }));
          }}
          error={errors.phone}
          required
          autoComplete="tel"
        />

        {merchant?.storeInfo?.termsAndConditionsURL && (
          <p className={styles.termsLink}>
            <a 
              href={merchant.storeInfo.termsAndConditionsURL}
              target="_blank"
              rel="noopener noreferrer"
            >
              {L.byProceedingText}
            </a>
          </p>
        )}

        {/* GDPR: Let customer manage/delete saved data */}
        {savedAddresses && savedAddresses.length > 0 && (
          <button
            type="button"
            className={styles.manageDataLink}
            onClick={() => setShowDeleteDialog(true)}
          >
            <LockIcon />
            {locale === 'sv' ? 'Hantera sparade uppgifter' : locale === 'da' ? 'Administrer gemte oplysninger' : locale === 'fi' ? 'Hallinnoi tallennettuja tietoja' : locale === 'en' ? 'Manage saved data' : 'Administrer lagrede opplysninger'}
          </button>
        )}

        <div className={styles.navigation}>
          <button type="button" className={styles.backButton} onClick={prevStep}>
            <BackIcon />
            {L.backButton}
          </button>
          <Button type="submit" loading={isLoading}>
            {L.continueButton}
          </Button>
        </div>
      </form>

      {/* GDPR Delete Confirmation Dialog */}
      {showDeleteDialog && (
        <div className={styles.overlay} onClick={() => !isDeleting && setShowDeleteDialog(false)}>
          <div className={styles.dialog} onClick={(e) => e.stopPropagation()}>
            {deleteSuccess ? (
              <p className={styles.successMsg}>{locale === 'sv' ? '✓ Alla sparade uppgifter har raderats' : locale === 'da' ? '✓ Alle gemte oplysninger er slettet' : locale === 'fi' ? '✓ Kaikki tallennetut tiedot on poistettu' : locale === 'en' ? '✓ All saved data has been deleted' : '✓ Alle lagrede opplysninger er slettet'}</p>
            ) : (
              <>
                <h3 className={styles.dialogTitle}>{locale === 'sv' ? 'Radera sparade uppgifter?' : locale === 'da' ? 'Slet gemte oplysninger?' : locale === 'fi' ? 'Poista tallennetut tiedot?' : locale === 'en' ? 'Delete saved data?' : 'Slett lagrede opplysninger?'}</h3>
                <p className={styles.dialogText}>
                  {L.deleteDataConfirm}
                </p>
                <div className={styles.dialogActions}>
                  <button
                    type="button"
                    className={styles.dialogCancel}
                    onClick={() => setShowDeleteDialog(false)}
                    disabled={isDeleting}
                  >
                    {locale === 'sv' ? 'Avbryt' : locale === 'da' ? 'Annuller' : locale === 'fi' ? 'Peruuta' : locale === 'en' ? 'Cancel' : 'Avbryt'}
                  </button>
                  <button
                    type="button"
                    className={styles.dialogDelete}
                    onClick={handleDeleteData}
                    disabled={isDeleting}
                  >
                    {isDeleting ? (locale === 'sv' ? 'Raderar…' : locale === 'da' ? 'Sletter…' : locale === 'fi' ? 'Poistetaan…' : locale === 'en' ? 'Deleting…' : 'Sletter…') : (locale === 'sv' ? 'Radera allt' : locale === 'da' ? 'Slet alt' : locale === 'fi' ? 'Poista kaikki' : locale === 'en' ? 'Delete all' : 'Slett alt')}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function BackIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function LockIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
      <rect x="3" y="7" width="10" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M5 7V5a3 3 0 116 0v2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  );
}
